import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {AntibodyComponentType} from 'antibodies/AntibodyConstants';
import {useAntibodyFormatsQuery} from 'antibodies/hooks/useAntibodyFormatsQuery';
import {useImplementedAntibodySchemaInterfacesQuery} from 'antibodies/hooks/useImplementedAntibodySchemaInterfacesQuery';
import AntibodyConfigurationTable from 'antibodies/schema-configuration/AntibodyConfigurationTable';
import AntibodyFormatPicker from 'antibodies/schema-configuration/AntibodyFormatPicker';
import {AntibodyTileProps} from 'antibodies/schema-configuration/AntibodyFormatPicker';
import {
  ANTIBODY_CONFIGURATION_TABLE_QUERY,
  ANTIBODY_SCHEMA_CREATION_BLOCKED_BUILTIN_FORMAT_IDS,
  ANTIBODY_SCHEMA_INTERFACE_ID,
  AntibodyChainSchemaFieldSpec,
  DraftImplementedAntibodyInterface,
  filterAntibodyComponentSchemas,
  TagSchemaWithInterfacesForAntibodyConfig,
} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import {buildFormatSchemaUsageMap} from 'antibodies/schema-configuration/buildFormatSchemaUsageMap';
import {canUseBiologicsFeature} from 'antibodies/util/biologicsCapability';
import {assert} from 'Asserts';
import {BiologicsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {AntibodyDomainType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {Header3} from 'ui-library/Headers';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {immutableReplace} from 'util/ImmutableListUtil';

import {AntibodyFormatGraphQL} from '../../../graphql-fragments/antibodies/__generated/AntibodyFormatGraphQL';
import {TagSchemaSummaryGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';
import {AntibodyConfigurationTableQuery} from './__generated/AntibodyConfigurationTableQuery';

function findSelectedFormat(
  formatId: null | string,
  formats: Array<AntibodyFormatGraphQL> | null
): AntibodyFormatGraphQL | null {
  if (formatId == null || formats == null) {
    return null;
  } else {
    return formats.find(({id}) => id === formatId) ?? null;
  }
}

export interface AntibodyConfigurationProps {
  antibodyInterfaceImplementation: DraftImplementedAntibodyInterface;
  disabled: boolean;
  onChange: (config: DraftImplementedAntibodyInterface) => void;
  /* All tag schemas that currently exist on the registry */
  allSchemas: Array<TagSchemaSummaryGraphQL>;
  isCreatingSchema: boolean;
}

export default function AntibodySchemaConfiguration({
  antibodyInterfaceImplementation,
  disabled,
  onChange,
  allSchemas,
  isCreatingSchema,
}: AntibodyConfigurationProps): JSX.Element {
  const {status, antibodyFormats, refetch} = useAntibodyFormatsQuery();
  const selectedFormat = useMemo(
    () => findSelectedFormat(antibodyInterfaceImplementation.formatId ?? null, antibodyFormats),
    [antibodyFormats, antibodyInterfaceImplementation]
  );
  const schemaInterfacesQuery = useBenchlingQuery<AntibodyConfigurationTableQuery>(
    ANTIBODY_CONFIGURATION_TABLE_QUERY
  );
  const {
    status: schemaInterfacesStatus,
    implementedAntibodySchemaInterfaces,
    refetch: refetchSchemaInterfaces,
  } = useImplementedAntibodySchemaInterfacesQuery();
  const formatSchemaUsageMap = useMemo(
    () =>
      schemaInterfacesStatus === QueryResultType.SUCCESS
        ? buildFormatSchemaUsageMap(implementedAntibodySchemaInterfaces)
        : undefined,
    [schemaInterfacesStatus, implementedAntibodySchemaInterfaces]
  );

  // Refetch schema-format associations when the schema list changes (e.g., after creating or archiving a schema)
  useEffect(() => {
    refetchSchemaInterfaces().catch(() => {});
  }, [allSchemas, refetchSchemaInterfaces]);

  const allSchemasWithInterfaces: Array<TagSchemaWithInterfacesForAntibodyConfig> = useMemo(() => {
    const schemasById = allSchemas.reduce(
      (acc, schema) => {
        acc[schema.id] = schema;
        return acc;
      },
      {} as Record<string, TagSchemaSummaryGraphQL>
    );
    if (schemaInterfacesQuery.type !== QueryResultType.SUCCESS || schemaInterfacesQuery.data == null) {
      return [];
    }
    return schemaInterfacesQuery.data.tagSchemas.map((tagSchema) => ({
      ...schemasById[tagSchema.id],
      interfaces: tagSchema.interfaces,
    }));
  }, [allSchemas, schemaInterfacesQuery]);

  const [isCreationPaneOpen, setIsCreationPaneOpen] = useState(false);

  const findAndSelectFormat = useCallback(
    (formatId: string | null, formats: Array<AntibodyFormatGraphQL> | null): void => {
      const format = findSelectedFormat(formatId, formats);
      if (formatId == null || format == null) {
        onChange({
          interfaceId: ANTIBODY_SCHEMA_INTERFACE_ID,
        });
      } else {
        onChange({
          interfaceId: ANTIBODY_SCHEMA_INTERFACE_ID,
          formatId,
          domainSchemaFieldSpecs: format.domains.map(({type, specificity}) => ({
            schemaOrInterfaceId: null,
            domainType: type,
            specificity,
          })),
          chainSchemaFieldSpecs: format.chains.map(({label}) => ({
            schemaOrInterfaceId: null,
            label,
          })),
        });
      }
    },
    [onChange]
  );
  const onChangeFormat = useCallback(
    (formatId: string | null): void => {
      if (formatId === (antibodyInterfaceImplementation.formatId ?? null)) {
        return;
      }
      findAndSelectFormat(formatId, antibodyFormats);
    },
    [findAndSelectFormat, antibodyFormats, antibodyInterfaceImplementation.formatId]
  );
  // When a format is created, we set the newly created format as selected. We first need to refetch all the
  // formats so the new format is included. When we call the selection function, React state hasn't updated,
  // so we pass the result of the refetch along with the new format's id.
  const onFormatCreated = useCallback(
    async (formatId: string): Promise<void> => {
      const updatedFormats = await refetch();
      findAndSelectFormat(formatId, updatedFormats);
    },
    [refetch, findAndSelectFormat]
  );

  // When initially configuring an antibody schema with enforced data model, select default domain and chain schemas
  useEffect(() => {
    const domainSchemas = filterAntibodyComponentSchemas(
      allSchemasWithInterfaces,
      AntibodyComponentType.AA_DOMAIN
    );
    const chainSchemas = filterAntibodyComponentSchemas(
      allSchemasWithInterfaces,
      AntibodyComponentType.AA_CHAIN
    );
    const defaultDomainSchema = domainSchemas.length === 1 ? domainSchemas[0] : null;
    const defaultChainSchema = chainSchemas.length === 1 ? chainSchemas[0] : null;
    if (
      disabled ||
      !canUseBiologicsFeature(BiologicsFeature.BIOLOGICS_EDIT) ||
      selectedFormat == null ||
      schemaInterfacesQuery.type !== QueryResultType.SUCCESS ||
      schemaInterfacesQuery.data == null ||
      (defaultDomainSchema == null && defaultChainSchema == null)
    ) {
      return;
    }

    const currentDomainSpecs = antibodyInterfaceImplementation.domainSchemaFieldSpecs ?? [];
    const currentChainSpecs = antibodyInterfaceImplementation.chainSchemaFieldSpecs ?? [];
    let updatedDomainSpecs = currentDomainSpecs;
    let updatedChainSpecs = currentChainSpecs;
    if (defaultDomainSchema != null) {
      updatedDomainSpecs = currentDomainSpecs.map((spec) => {
        if (spec.schemaOrInterfaceId == null) {
          return {...spec, schemaOrInterfaceId: defaultDomainSchema.id};
        }
        return spec;
      });
    }
    if (defaultChainSchema != null) {
      updatedChainSpecs = currentChainSpecs.map((spec) => {
        if (spec.schemaOrInterfaceId == null) {
          return {...spec, schemaOrInterfaceId: defaultChainSchema.id};
        }
        return spec;
      });
    }

    onChange({
      ...antibodyInterfaceImplementation,
      domainSchemaFieldSpecs: updatedDomainSpecs,
      chainSchemaFieldSpecs: updatedChainSpecs,
    });
    // Defaults should only be applied when the data is first loaded or when the selected format changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFormat?.id, schemaInterfacesQuery.type]);

  if (status === QueryResultType.PENDING || schemaInterfacesQuery.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (status !== QueryResultType.SUCCESS) {
    return (
      <div>
        <InlineBanner bannerType={InlineBannerType.ERROR} text="Error loading protein formats" />
      </div>
    );
  } else if (schemaInterfacesQuery.type !== QueryResultType.SUCCESS) {
    return (
      <div>
        <InlineBanner bannerType={InlineBannerType.ERROR} text="Error loading AA sequence schemas" />
      </div>
    );
  }
  const formatsProps: Array<AntibodyTileProps> = [...antibodyFormats]
    .filter(
      (format) => !isCreatingSchema || !ANTIBODY_SCHEMA_CREATION_BLOCKED_BUILTIN_FORMAT_IDS.has(format.id)
    )
    .sort((a, b) => {
      // Built-in formats first (preserve original order among them)
      if (a.builtIn !== b.builtIn) {
        return a.builtIn ? -1 : 1;
      }
      // Among custom formats, non-archived before archived
      if (a.archiveData.archived !== b.archiveData.archived) {
        return a.archiveData.archived ? 1 : -1;
      }
      // Alphabetize custom formats within each group
      if (!a.builtIn) {
        return a.name.localeCompare(b.name);
      }
      return 0;
    })
    .map((format) => ({
      format,
      disabled: false,
    }));

  const onChangeDomainSchema = (
    domainType: AntibodyDomainType,
    specificity: string | null,
    schemaOrInterfaceId: string | null
  ): void => {
    const domainSchemaFieldSpecs = antibodyInterfaceImplementation.domainSchemaFieldSpecs;
    assert(domainSchemaFieldSpecs != null, 'Domain schema field specs and format should be defined');
    const specToReplaceIndex = domainSchemaFieldSpecs.findIndex(
      (spec) => spec.domainType === domainType && spec.specificity === specificity
    );
    const newDomainSchemaFieldSpecs = immutableReplace(domainSchemaFieldSpecs, specToReplaceIndex, {
      schemaOrInterfaceId,
      domainType,
      specificity,
    });
    onChange({
      ...antibodyInterfaceImplementation,
      domainSchemaFieldSpecs: newDomainSchemaFieldSpecs,
    });
  };

  const onChangeChainSchema = (label: string, schemaOrInterfaceId: string | null): void => {
    const chainSchemaFieldSpecs = antibodyInterfaceImplementation.chainSchemaFieldSpecs;
    assert(chainSchemaFieldSpecs != null, 'Chain schema field specs and format should be defined');
    const specToReplaceIndex = chainSchemaFieldSpecs.findIndex((spec) => spec.label === label);
    const newChainSchemaFieldSpecs = immutableReplace<AntibodyChainSchemaFieldSpec>(
      chainSchemaFieldSpecs,
      specToReplaceIndex,
      {
        schemaOrInterfaceId,
        label,
      }
    );
    onChange({
      ...antibodyInterfaceImplementation,
      chainSchemaFieldSpecs: newChainSchemaFieldSpecs,
    });
  };

  return (
    <>
      <Header3 className={css(styles.formatPickerHeader)}>Format</Header3>
      <AntibodyFormatPicker
        antibodyTileProps={formatsProps}
        disabled={disabled}
        selectedFormat={selectedFormat?.id ?? null}
        setSelectedFormat={onChangeFormat}
        enableCreateNew={isCreatingSchema}
        showAuditLogButton={true}
        enableFormatManagement={true}
        onFormatCreated={onFormatCreated}
        onCreationPaneOpenChange={setIsCreationPaneOpen}
        onArchiveChange={async () => {
          await Promise.all([refetch(), refetchSchemaInterfaces()]);
        }}
        formatSchemaUsageMap={formatSchemaUsageMap}
        analyticsMetadata={{
          actionOrigin: ActionOrigin.SETTINGS_ENTITY_SCHEMA_PAGE,
          eventGroupId: analytics.makeAnalyticsEventId(),
        }}
      />
      {selectedFormat != null && !isCreationPaneOpen && (
        <AntibodyConfigurationTable
          format={selectedFormat}
          allSchemas={allSchemasWithInterfaces}
          disabled={disabled}
          onChangeDomainSchema={onChangeDomainSchema}
          onChangeChainSchema={onChangeChainSchema}
          domainSchemaFieldSpecs={antibodyInterfaceImplementation.domainSchemaFieldSpecs ?? []}
          chainSchemaFieldSpecs={antibodyInterfaceImplementation.chainSchemaFieldSpecs ?? []}
        />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  formatPickerHeader: {
    margin: '16px 0',
  },
});
